import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import {
  Description,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from './common/Catalyst/fieldset';
import { FieldPicker } from './common/Custom/FieldPicker';
import { Button } from './common/Catalyst/button';
import { Text } from './common/Catalyst/text';
import ShowAlert from './ShowAlert';
import LoadingSpinner from './LoadingSpinner';
import { bankService } from './clients/ApiClients';
import { PayerFilePreviewReturn } from './clients/rust-api-client';
import { getBankErrorParts } from './utils/bankHelpers';
import {
  getUserTypeFromToken,
  getCompanyIdFromToken,
} from './utils/TokenUtils';
import { canManageBankConnections } from './constants/UserTypes';

interface PayerFileDialogProps {
  open: boolean;
  onClose: () => void;
  hoaId: string;
  /** Called after a submission is recorded, to refresh the history list. */
  onCompleted: () => void;
}

interface AlertState {
  title: string;
  message: string;
}

// One selectable bank connection (only shown when the company has more than
// one — otherwise the backend resolves it implicitly).
interface ConnOption {
  id: string;
  label: string;
}

/**
 * Title + message for an error from the preview/submit calls. The payer
 * endpoints return the structured `BankErrorBody` (`{ code, message }`), and
 * the two 400s worth special-casing both arrive as `invalid_request`:
 * a connection with no lockbox id, and an ambiguous connection.
 */
const payerError = (err: unknown, fallback: string): AlertState => {
  const { status, code, body } = getBankErrorParts(err);
  const text = body || '';
  if (text.includes('lockbox_company_id')) {
    return {
      title: 'Lockbox Company ID missing',
      message:
        'Set the Lockbox Company ID on the bank connection (Banking → the connection’s Edit) before submitting a payer file.',
    };
  }
  if (text.includes('multiple bank connections')) {
    return {
      title: 'Choose a bank connection',
      message:
        'This company has more than one bank connection — pick the one that holds the lockbox.',
    };
  }
  if (code === 'bank_unreachable' || status === 503) {
    return {
      title: 'Bank temporarily unavailable',
      message:
        body ||
        'The bank is unreachable from this environment. Submitting requires the deployed environment.',
    };
  }
  if (code === 'bank_error' || status === 502) {
    return {
      title: 'Bank error',
      message: body || 'The bank rejected the upload. Please try again later.',
    };
  }
  return { title: fallback, message: body || 'Please try again.' };
};

const PayerFileDialog: React.FC<PayerFileDialogProps> = ({
  open,
  onClose,
  hoaId,
  onCompleted,
}) => {
  const [connOptions, setConnOptions] = useState<ConnOption[]>([]);
  const [connId, setConnId] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<PayerFilePreviewReturn | null>(null);
  const [error, setError] = useState<AlertState | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const runPreview = useCallback(
    async (connectionId?: string) => {
      try {
        setLoading(true);
        setError(null);
        const resp = await bankService.previewPayerFile({
          hoaId,
          payerFileRequest: { bank_connection_id: connectionId || null },
        });
        setPreview(resp.data);
      } catch (err) {
        console.error('Error previewing payer file:', err);
        setPreview(null);
        setError(payerError(err, "Couldn't build the payer file"));
      } finally {
        setLoading(false);
      }
    },
    [hoaId]
  );

  // On open: work out whether a connection has to be chosen, then preview.
  //
  // `listCompanyConnections` is Owner/SuperAdmin-only on the backend, but an
  // Admin may submit payer files — so for Admins the candidate connections are
  // derived from the HOA's assigned accounts (`listHoaAccounts` carries
  // `bank_connection_id`, needs no bank call, and is readable by the same
  // audience). With one candidate (the common case) the connection is left
  // implicit and the preview runs immediately.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    setPreview(null);
    setError(null);
    setConnId('');
    setConnOptions([]);

    (async () => {
      setLoading(true);
      let options: ConnOption[] = [];
      try {
        const companyId = getCompanyIdFromToken();
        if (canManageBankConnections(getUserTypeFromToken()) && companyId) {
          const resp = await bankService.listCompanyConnections({ companyId });
          options = resp.data
            .filter((c) => c.is_active)
            .map((c) => ({
              id: c.id,
              label: c.label || c.provider_key,
            }));
        } else {
          const resp = await bankService.listHoaAccounts({ hoaId });
          const seen = new Set<string>();
          options = resp.data.accounts
            .filter((a) => {
              if (seen.has(a.bank_connection_id)) return false;
              seen.add(a.bank_connection_id);
              return true;
            })
            .map((a) => ({
              id: a.bank_connection_id,
              label: `Connection …${a.bank_connection_id.slice(-6)}`,
            }));
        }
      } catch (err) {
        // Non-fatal: fall through with no options and let the preview call
        // report the real problem (including an ambiguous connection).
        console.error('Error loading payer connection options:', err);
      }
      if (cancelled) return;
      setConnOptions(options);
      setLoading(false);
      if (options.length <= 1) {
        await runPreview(options[0]?.id);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, hoaId, runPreview]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!preview) return;
    try {
      setSubmitting(true);
      setError(null);
      const resp = await bankService.submitPayerFile({
        hoaId,
        payerFileRequest: { bank_connection_id: preview.bank_connection_id },
      });
      onCompleted();
      if (resp.data.status === 'failed') {
        // Terminal failure — keep the dialog open with the bank's reason; the
        // history row records the attempt either way.
        setError({
          title: 'Payer file rejected',
          message:
            resp.data.error_message ||
            'The bank did not accept the file. See the submission history for details.',
        });
        return;
      }
      // `processing` is the normal resting state for an accepted upload (the
      // bank validates the records separately), so anything but `failed` is a
      // success — the history row shows where it got to.
      onClose();
    } catch (err) {
      console.error('Error submitting payer file:', err);
      setError(payerError(err, "Couldn't submit the payer file"));
    } finally {
      setSubmitting(false);
    }
  };

  const needsConnectionChoice = connOptions.length > 1 && !preview;

  return (
    <Dialog open={open} onClose={handleClose} size="3xl">
      <DialogTitle>Payer File</DialogTitle>
      <DialogDescription>
        {preview
          ? 'This is the exact file that will be sent to the bank. Review it, then submit.'
          : 'Build this HOA’s payer file — one record per home — and send it to the bank’s lockbox.'}
      </DialogDescription>
      <DialogBody>
        {error && (
          <div className="mb-4">
            <ShowAlert
              alertType="error"
              alertTitle={error.title}
              alertMessage={error.message}
              isVisible={!!error}
              onClose={() => setError(null)}
              timeout={12000}
              isInline={true}
            />
          </div>
        )}

        {loading ? (
          <LoadingSpinner />
        ) : needsConnectionChoice ? (
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Bank connection</Label>
                <Description>
                  This company has more than one bank connection. Choose the one
                  whose lockbox should receive the file.
                </Description>
                <FieldPicker
                  label="Bank connection"
                  value={connId}
                  onChange={setConnId}
                  placeholder="Select a connection"
                  options={connOptions.map((c) => ({
                    value: c.id,
                    label: c.label,
                  }))}
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        ) : preview ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-x-8 gap-y-2">
              <div>
                <Text className="text-xs">Records</Text>
                <span className="text-lg font-semibold tabular-nums">
                  {preview.record_count}
                </span>
              </div>
              <div>
                <Text className="text-xs">File date</Text>
                <span className="text-lg font-semibold tabular-nums">
                  {preview.file_date}
                </span>
              </div>
              <div>
                <Text className="text-xs">Lockbox ID</Text>
                <span className="text-lg font-semibold tabular-nums">
                  {preview.lockbox_company_id}
                </span>
              </div>
            </div>
            <pre className="max-h-72 overflow-auto rounded-md bg-stone-200 dark:bg-zinc-700 p-3 text-xs font-mono whitespace-pre">
              {preview.file_content}
            </pre>
          </div>
        ) : (
          <Text>The payer file could not be built. See the error above.</Text>
        )}
      </DialogBody>
      <DialogActions>
        {preview ? (
          <>
            <Button plain onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button color="sky" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Sending…' : 'Submit to bank'}
            </Button>
          </>
        ) : (
          <>
            <Button plain onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              color="sky"
              onClick={() => runPreview(connId)}
              disabled={loading || (needsConnectionChoice && !connId)}
            >
              {loading ? 'Building…' : 'Build preview'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PayerFileDialog;
